import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { clsb, clsx } from 'js/utils/classNames.util';
import { useEffectOnce } from 'js/hooks/useEffectOnce';

import * as Types from './types';
import withField from '../withField';
import { Control } from '../Control';
import ValidationIcon from '../Icons/ValidationIcon';
import { AutocompleteOptions } from './components/AutocompleteOptions';
import { VISIBLE_ITEMS_COUNT, MIN_SYMBOLS_TO_SEARCH } from './constants';

import style from './Autocomplete.scss';

const AutocompleteBare: React.FC<Types.IAutocompleteProps> = (props) => {
    const {
        /* rff render props */
        input = {},
        meta = {},

        'aria-label': ariaLabel,
        bottomHint,
        className,
        classNames,
        disabled,
        error: customError,
        hint,
        id,
        infoTooltip,
        isLabelAsteriskEnabled,
        isPlaceholderAsteriskEnabled,
        label,
        minSymbolsToSearch = MIN_SYMBOLS_TO_SEARCH,
        name,
        noOptionsMessage,
        onClick,
        onMouseDown,
        onOptionSelect,
        onSearchChange,
        Option,
        options,
        placeholder,
        readOnly,
        visibleOptionsCount = VISIBLE_ITEMS_COUNT
    } = props;
    const translate = useTranslations();
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const preventClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => event.preventDefault(), []);

    const initialOption = useMemo((): Types.IAutocompleteOption | null => options
        .find(option => option.value === input.value) || null,
        [input.value, options]
    );
    const [chosenOption, setChosenOption] = useState<Types.IAutocompleteOption | null>(initialOption);
    const [isListOpened, setIsListOpened] = useState<boolean>(false);
    const [list, setList] = useState<Types.IAutocompleteOption[]>(options);
    const [activeValue, setActiveValue] = useState<string>();
    const { touched, active, error, validating, submitting } = meta;

    // region input field processing
    useEffectOnce(() => {
        if (!inputRef.current || !initialOption) {
            return;
        }

        inputRef.current.value = initialOption.label;
    }, [initialOption]);

    const onInputFocus = useCallback((event: React.FocusEvent<HTMLInputElement>): void => {
        input.onFocus?.(event);

        if (inputRef.current?.value) {
            setIsListOpened(true);
        }
    }, [input]);

    const onInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const { value } = event.target;

        if (value.length < minSymbolsToSearch) {
            setIsListOpened(false);
            return;
        }

        if (onSearchChange) {
            const nextList = await onSearchChange(value);

            if (!nextList) {
                setIsListOpened(false);
                return;
            }

            setIsListOpened(true);
            setList(nextList);
        } else {
            setIsListOpened(true);
            setList(options.filter(item => item.label.toLowerCase().includes(value.toLowerCase())));
        }

        optionsContainerRef.current?.scrollTo(0, 0);
    }, [options, onSearchChange, minSymbolsToSearch]);

    // endregion

    // region options processing
    const handleCloseList = useCallback((currentOption?: Types.IAutocompleteOption): void => {
        setIsListOpened(false);
        setActiveValue(undefined);
        setList(options);

        const inputValue = currentOption?.label || chosenOption?.label || '';

        if (!inputRef.current) {
            return;
        }

        inputRef.current.value = inputValue;

        if (!inputValue) {
            input.onChange?.('');
            setChosenOption(null);
        }

        inputRef.current.blur();
        input.onBlur?.();
    }, [input, options, chosenOption]);

    const onOptionClick = useCallback(async (option: Types.IAutocompleteOption): Promise<void> => {
        const handleOptionChange = () => {
            input.onChange?.(option.value);
            setChosenOption(option);
            handleCloseList(option);
        };

        if (!onOptionSelect) {
            handleOptionChange();

            return;
        }

        const newOptions = await onOptionSelect(option);

        if (newOptions?.length) {
            setList(newOptions);
        } else {
            handleOptionChange();
        }
    }, [onOptionSelect, input, handleCloseList]);

    // endregion

    // region events processing
    const handleDocumentKeyDown = useCallback((event: KeyboardEvent): void => {
        if (isListOpened && event.key === 'Tab') {
            handleCloseList();

            return;
        }

        if (event.key === 'Escape' && inputRef.current && containerRef.current?.contains(event.target as Node)) {
            inputRef.current.value = '';
            input.onChange?.('');
            setChosenOption(null);
            minSymbolsToSearch > 0 && setIsListOpened(false);
        }
    }, [input, isListOpened, minSymbolsToSearch, handleCloseList]);

    const handleDocumentClick = useCallback((event: Event): void => {
        if (isListOpened && !containerRef.current?.contains(event.target as Node)) {
            handleCloseList();
        }
    }, [isListOpened, handleCloseList]);

    useEffect(() => {
        document.addEventListener('keydown', handleDocumentKeyDown);
        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [handleDocumentKeyDown, handleDocumentClick]);

    // endregion

    return (
        <div
            ref={containerRef}
            className={clsx([
                className,
                'cms-autocomplete-field',
                clsb([style, classNames], ['container'])
            ])}
        >
            <Control
                id={id}
                hint={hint}
                label={label}
                animate={true}
                onClick={onClick}
                readOnly={readOnly}
                classNames={classNames}
                bottomHint={bottomHint}
                infoTooltip={infoTooltip}
                className={style.control}
                disabled={disabled || readOnly}
                isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                meta={{ ...meta, ...isListOpened && { active: true }, ...customError && { customError } }}
            >
                <input
                    id={id}
                    type="text"
                    name={name}
                    ref={inputRef}
                    readOnly={readOnly}
                    onBlur={input.onBlur}
                    onFocus={onInputFocus}
                    onChange={onInputChange}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    onMouseDown={readOnly ? preventClick : onMouseDown}
                    disabled={disabled || readOnly || submitting || validating}
                    className={`${clsb([style, classNames], ['input'])} cms-autocomplete-field-input`}
                    aria-label={ariaLabel || translate('playtech.mobile.system.autocomplete.aria-label')}
                    {...placeholder && { placeholder: `${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}` } }
                    role="combobox"
                    aria-live="polite"
                    aria-autocomplete="list"
                    aria-expanded={isListOpened}
                    {...activeValue && { 'aria-activedescendant': `${id}_${activeValue}` }}
                />

                {!isListOpened && touched && !active && !validating && !readOnly && (
                    <ValidationIcon
                        animate={true}
                        isValid={!customError && !error}
                        aria-label={customError || error}
                        tabIndex={customError || error ? 0 : -1}
                        className={clsb([style, classNames], ['validationIcon'])}
                    />
                )}

                {isListOpened && (
                    <AutocompleteOptions
                        fieldId={id}
                        options={list}
                        Option={Option}
                        classNames={classNames}
                        activeValue={activeValue}
                        onOptionClick={onOptionClick}
                        setActiveValue={setActiveValue}
                        chosenValue={chosenOption?.value}
                        noOptionsMessage={noOptionsMessage}
                        visibleOptionsCount={visibleOptionsCount}
                        optionsContainerRef={optionsContainerRef}
                    />
                )}
            </Control>
        </div>
    );
};

export const Autocomplete = withField(AutocompleteBare);
